import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Kiosk Missed Punch Wizard
 *
 * A guided flow for kiosk employees to add missed punches with smart validation.
 * Adapted from MissedPunchWizard for kiosk context (PIN-based, no auth tokens).
 *
 * Flow:
 * 1. Select what happened (forgot to clock in, break start, break end, clock out)
 * 2. Based on current session state, validate the selection makes sense
 * 3. Enter the time (defaults to current day, only time picker for simplicity)
 * 4. Provide reason (required)
 * 5. Submit - punch is immediately applied
 */
import { useState, useMemo } from 'react';
import { format, set, subMinutes } from 'date-fns';
import { X, Clock, LogIn, LogOut, Coffee, ArrowRight, ArrowLeft, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
const MISSED_PUNCH_OPTIONS = [
    {
        id: 'CLOCK_IN',
        title: 'I forgot to clock in',
        description: 'I arrived at work but forgot to record my start time',
        icon: LogIn,
        requiredState: { mustBeClockedOut: true },
        errorMessage: 'You are already clocked in. Did you mean to add a different punch?',
    },
    {
        id: 'START_BREAK',
        title: 'I forgot to start my break',
        description: 'I went on break but forgot to record when it started',
        icon: Coffee,
        requiredState: { mustBeClockedIn: true, mustNotBeOnBreak: true },
        errorMessage: 'You must be clocked in (and not already on break) to add a missed break start.',
    },
    {
        id: 'END_BREAK',
        title: 'I forgot to end my break',
        description: 'I returned from break but forgot to record when it ended',
        icon: Clock,
        requiredState: { mustBeClockedIn: true, mustBeOnBreak: true },
        errorMessage: 'You must be on break to add a missed break end.',
    },
    {
        id: 'CLOCK_OUT',
        title: 'I forgot to clock out',
        description: 'I finished work but forgot to record my end time',
        icon: LogOut,
        requiredState: { mustBeClockedIn: true, mustNotBeOnBreak: true },
        errorMessage: 'You must be clocked in (and not on break) to add a missed clock out.',
    },
];
export function KioskMissedPunchWizard({ open, onClose, onSuccess, sessionStatus, onSubmit, }) {
    const [step, setStep] = useState('select');
    const [selectedType, setSelectedType] = useState(null);
    const [selectedTime, setSelectedTime] = useState(() => {
        // Default to 5 minutes ago as a reasonable default
        const fiveMinutesAgo = subMinutes(new Date(), 5);
        return format(fiveMinutesAgo, 'HH:mm');
    });
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // Reset state when dialog closes
    const handleClose = () => {
        setStep('select');
        setSelectedType(null);
        setSelectedTime(format(subMinutes(new Date(), 5), 'HH:mm'));
        setReason('');
        setError(null);
        onClose();
    };
    // Convert kiosk session status to punch session format
    const sessionState = useMemo(() => {
        if (!sessionStatus) {
            return { isClockedIn: false, isOnBreak: false };
        }
        return {
            isClockedIn: sessionStatus.status === 'CLOCKED_IN' || sessionStatus.status === 'ON_BREAK',
            isOnBreak: sessionStatus.status === 'ON_BREAK',
        };
    }, [sessionStatus]);
    // Check if a punch option is valid based on current session state
    const getOptionValidity = (option) => {
        const { isClockedIn, isOnBreak } = sessionState;
        const { requiredState } = option;
        if (requiredState.mustBeClockedIn && !isClockedIn) {
            return { valid: false, message: option.errorMessage };
        }
        if (requiredState.mustBeClockedOut && isClockedIn) {
            return { valid: false, message: option.errorMessage };
        }
        if (requiredState.mustBeOnBreak && !isOnBreak) {
            return { valid: false, message: option.errorMessage };
        }
        if (requiredState.mustNotBeOnBreak && isOnBreak) {
            return { valid: false, message: option.errorMessage };
        }
        return { valid: true };
    };
    // Get valid options based on current state
    const validOptions = useMemo(() => {
        return MISSED_PUNCH_OPTIONS.map(option => ({
            ...option,
            validity: getOptionValidity(option),
        }));
    }, [sessionState]);
    // Calculate the timestamp from the selected time
    const getTimestamp = () => {
        const parts = selectedTime.split(':');
        const hours = parseInt(parts[0] ?? '0', 10);
        const minutes = parseInt(parts[1] ?? '0', 10);
        const timestamp = set(new Date(), {
            hours,
            minutes,
            seconds: 0,
            milliseconds: 0,
        });
        return timestamp.toISOString();
    };
    // Validate the selected time
    const validateTime = () => {
        const now = new Date();
        const timestamp = new Date(getTimestamp());
        // Can't be in the future
        if (timestamp > now) {
            return { valid: false, message: 'The time cannot be in the future.' };
        }
        // Can't be before start of today (kiosk only allows today)
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        if (timestamp < todayStart) {
            return { valid: false, message: 'For missed punches from previous days, please contact your manager.' };
        }
        return { valid: true };
    };
    const timeValidation = validateTime();
    // Handle selecting a punch type
    const handleSelectType = (type) => {
        const option = validOptions.find(o => o.id === type);
        if (option?.validity.valid) {
            setSelectedType(type);
            setError(null);
            setStep('time');
        }
    };
    // Handle going back
    const handleBack = () => {
        if (step === 'time') {
            setStep('select');
            setSelectedType(null);
        }
        else if (step === 'reason') {
            setStep('time');
        }
        else if (step === 'confirm') {
            setStep('reason');
        }
    };
    // Handle continuing to next step
    const handleNext = () => {
        if (step === 'time') {
            if (!timeValidation.valid) {
                setError(timeValidation.message || 'Invalid time');
                return;
            }
            setError(null);
            setStep('reason');
        }
        else if (step === 'reason') {
            if (reason.trim().length < 10) {
                setError('Please provide a reason with at least 10 characters.');
                return;
            }
            setError(null);
            setStep('confirm');
        }
    };
    // Handle form submission
    const handleSubmit = async () => {
        if (!selectedType)
            return;
        setIsSubmitting(true);
        setError(null);
        try {
            await onSubmit({
                action: selectedType,
                timestamp: getTimestamp(),
                isMissedPunch: true,
                missedPunchReason: reason.trim(),
                requestedAt: new Date().toISOString(),
            });
            onSuccess();
            handleClose();
        }
        catch (err) {
            console.error('Failed to submit missed punch:', err);
            setError(err instanceof Error ? err.message : 'Failed to submit missed punch');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    if (!open)
        return null;
    const selectedOption = MISSED_PUNCH_OPTIONS.find(o => o.id === selectedType);
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50", children: _jsxs("div", { className: "relative w-full max-w-lg rounded-lg bg-white shadow-xl mx-4", children: [_jsxs("div", { className: "flex items-center justify-between border-b border-gray-200 px-6 py-4", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "rounded-lg bg-blue-100 p-2", children: _jsx(Clock, { className: "h-5 w-5 text-blue-600" }) }), _jsxs("div", { children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "Add Missed Punch" }), _jsxs("p", { className: "text-sm text-gray-600", children: [step === 'select' && 'What happened?', step === 'time' && 'When did this happen?', step === 'reason' && 'Why did you miss this punch?', step === 'confirm' && 'Review and submit'] })] })] }), _jsx("button", { onClick: handleClose, className: "rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600", disabled: isSubmitting, children: _jsx(X, { className: "h-5 w-5" }) })] }), _jsx("div", { className: "flex items-center gap-2 px-6 pt-4", children: ['select', 'time', 'reason', 'confirm'].map((s, idx) => (_jsxs("div", { className: "flex items-center", children: [_jsx("div", { className: `h-2 w-2 rounded-full transition-colors ${step === s
                                    ? 'bg-blue-600'
                                    : idx < ['select', 'time', 'reason', 'confirm'].indexOf(step)
                                        ? 'bg-blue-400'
                                        : 'bg-gray-200'}` }), idx < 3 && (_jsx("div", { className: `mx-1 h-0.5 w-8 transition-colors ${idx < ['select', 'time', 'reason', 'confirm'].indexOf(step)
                                    ? 'bg-blue-400'
                                    : 'bg-gray-200'}` }))] }, s))) }), _jsxs("div", { className: "p-6", children: [error && (_jsxs("div", { className: "mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3", children: [_jsx(AlertCircle, { className: "h-5 w-5 shrink-0 text-red-600" }), _jsx("p", { className: "text-sm text-red-700", children: error })] })), step === 'select' && (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "mb-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3", children: [_jsx(Info, { className: "h-5 w-5 shrink-0 text-blue-600 mt-0.5" }), _jsxs("div", { className: "text-sm text-blue-900", children: [_jsx("strong", { children: "Current status:" }), ' ', sessionStatus?.status === 'ON_BREAK'
                                                    ? 'On Break'
                                                    : sessionStatus?.status === 'CLOCKED_IN'
                                                        ? 'Clocked In'
                                                        : 'Not Clocked In'] })] }), validOptions.map((option) => {
                                    const Icon = option.icon;
                                    const isDisabled = !option.validity.valid;
                                    return (_jsxs("button", { onClick: () => handleSelectType(option.id), disabled: isDisabled, className: `w-full flex items-center gap-4 rounded-lg border p-4 text-left transition-all ${isDisabled
                                            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'}`, children: [_jsx("div", { className: `rounded-lg p-3 ${isDisabled ? 'bg-gray-100 text-gray-400' : 'bg-blue-100 text-blue-600'}`, children: _jsx(Icon, { className: "h-5 w-5" }) }), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: `font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`, children: option.title }), _jsx("div", { className: `text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`, children: isDisabled ? option.validity.message : option.description })] }), !isDisabled && (_jsx(ArrowRight, { className: "h-5 w-5 text-gray-400" }))] }, option.id));
                                })] })), step === 'time' && selectedOption && (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "rounded-lg border border-gray-200 bg-gray-50 p-4", children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(selectedOption.icon, { className: "h-5 w-5 text-blue-600" }), _jsx("span", { className: "font-medium text-gray-900", children: selectedOption.title })] }) }), _jsxs("div", { children: [_jsxs(Label, { htmlFor: "punchTime", className: "text-sm font-medium text-gray-700", children: ["What time did this happen? ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsxs("p", { className: "mt-1 text-sm text-gray-500", children: ["Select the time for today (", format(new Date(), 'MMMM d, yyyy'), ")"] }), _jsx("input", { id: "punchTime", type: "time", value: selectedTime, onChange: (e) => setSelectedTime(e.target.value), className: "mt-2 block w-full rounded-lg border border-gray-300 px-4 py-3 text-lg font-medium focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" }), !timeValidation.valid && (_jsx("p", { className: "mt-2 text-sm text-red-600", children: timeValidation.message }))] }), _jsxs("div", { className: "flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3", children: [_jsx(AlertCircle, { className: "h-5 w-5 shrink-0 text-amber-600 mt-0.5" }), _jsx("div", { className: "text-sm text-amber-900", children: "Please enter an accurate time. Falsifying time records may result in disciplinary action." })] })] })), step === 'reason' && selectedOption && (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "rounded-lg border border-gray-200 bg-gray-50 p-4", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(selectedOption.icon, { className: "h-5 w-5 text-blue-600" }), _jsx("span", { className: "font-medium text-gray-900", children: selectedOption.title })] }), _jsx("span", { className: "text-sm font-medium text-gray-600", children: format(new Date(getTimestamp()), 'h:mm a') })] }) }), _jsxs("div", { children: [_jsxs(Label, { htmlFor: "reason", className: "text-sm font-medium text-gray-700", children: ["Why did you miss this punch? ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx(Textarea, { id: "reason", value: reason, onChange: (e) => setReason(e.target.value), rows: 3, placeholder: "e.g., I was in a rush and forgot to punch in before starting work...", className: "mt-2" }), _jsx("p", { className: "mt-1 text-xs text-gray-500", children: "Minimum 10 characters. This will be visible to your manager in reports." })] })] })), step === 'confirm' && selectedOption && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "rounded-lg border border-green-200 bg-green-50 p-4", children: [_jsxs("div", { className: "flex items-center gap-3 mb-3", children: [_jsx(CheckCircle, { className: "h-5 w-5 text-green-600" }), _jsx("span", { className: "font-medium text-green-900", children: "Ready to submit" })] }), _jsxs("div", { className: "space-y-2 text-sm text-green-800", children: [_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { children: "Action:" }), _jsx("span", { className: "font-medium", children: selectedOption.title.replace('I forgot to ', '') })] }), _jsxs("div", { className: "flex justify-between", children: [_jsx("span", { children: "Time:" }), _jsx("span", { className: "font-medium", children: format(new Date(getTimestamp()), 'MMMM d, yyyy h:mm a') })] })] })] }), _jsxs("div", { className: "rounded-lg border border-gray-200 bg-gray-50 p-4", children: [_jsx(Label, { className: "text-sm font-medium text-gray-700", children: "Your reason:" }), _jsx("p", { className: "mt-1 text-sm text-gray-600", children: reason })] }), _jsxs("div", { className: "flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3", children: [_jsx(Info, { className: "h-5 w-5 shrink-0 text-blue-600 mt-0.5" }), _jsx("div", { className: "text-sm text-blue-900", children: "This punch will be recorded immediately. Your manager will be able to see missed punches in their reports." })] })] }))] }), _jsxs("div", { className: "flex items-center justify-between border-t border-gray-200 px-6 py-4", children: [_jsx("div", { children: step !== 'select' && (_jsxs(Button, { type: "button", variant: "ghost", onClick: handleBack, disabled: isSubmitting, children: [_jsx(ArrowLeft, { className: "mr-2 h-4 w-4" }), "Back"] })) }), _jsxs("div", { className: "flex gap-3", children: [_jsx(Button, { type: "button", variant: "outline", onClick: handleClose, disabled: isSubmitting, children: "Cancel" }), (step === 'time' || step === 'reason') && (_jsxs(Button, { type: "button", onClick: handleNext, disabled: (step === 'time' && !timeValidation.valid) || (step === 'reason' && reason.trim().length < 10), children: ["Continue", _jsx(ArrowRight, { className: "ml-2 h-4 w-4" })] })), step === 'confirm' && (_jsx(Button, { type: "button", onClick: handleSubmit, disabled: isSubmitting, children: isSubmitting ? 'Submitting...' : 'Submit Punch' }))] })] })] }) }));
}
